import React, {useState} from 'react';
import {
  capitalize, truncate, includes, uniq, compact,
} from 'lodash';
import cx from 'classnames';

import VideoThumbnail from './VideoThumbnail';
import AdminControls from './AdminControls';
import Link from '../../components/Link';
import {SaveButton} from '../../components/UpvoteButton';
import {ListAskedAt} from '../../components/ListAskedAt';
import FacePile from '../../components/FacePile';
import CollapserThing from '../../components/CollapserThing';
import {
  CaretIcon, ShortRightArrowIcon, SpeechBubbleIcon, CirclePlusIcon,
} from '../../components/svg';
import {CodingBadge} from '../../components/MemberBadge';
import {Badge, DifficultyToBadge} from '../../components/Badge';
import {useAnalytics} from '../../hooks';
import {getVideoThumbnailURL, onPressEnter, clampLines} from '../../util';

const MAX_TITLE_LENGTH = 200;

const CommentCount = ({question}) => {
  const {tracker} = useAnalytics();
  const hasComments = question.comments > 0;

  const onClick = (e) => {
    e.stopPropagation();
    if (hasComments) {
      tracker.questionListClickViewAnswers({question: question.id});
    } else {
      tracker.questionListClickAddAnswer({question: question.id});
    }
  };

  return (
    <Link
      href={`/questions/${question.id}/${question.slug}#answers`}
      className="mr-5 text-sm font-normal text-gray-500 hover:text-gray-600 flex items-center"
      onClick={onClick}
    >
      <SpeechBubbleIcon stroke="currentColor" className="h-5 w-5 mr-1.5" />
      {hasComments
        ? `${question.comments} answer${question.comments > 1 ? 's' : ''}`
        : 'Add answer'}
    </Link>
  );
};

const SeenButton = ({question}) => {
  const {tracker} = useAnalytics();

  const onClick = (e) => {
    e.stopPropagation();
    tracker.questionListClickSeenQuestion({id: question.id});
  };

  return (
    <Link
      href={`/questions/contribute?basedOn=${question.id}`}
      className="md:flex items-center mr-5 text-sm font-normal text-gray-500 hover:text-gray-600 flex items-center hidden"
      onClick={onClick}
    >
      <CirclePlusIcon stroke="currentColor" className="h-4 w-4 mr-2" />
      I was asked this
    </Link>
  );
};

const P = ({maxLines, className, children}) => (
  <p
    className={cx('relative text-sm text-gray-600', className)}
    style={clampLines(maxLines)}
  >
    {children}
  </p>
);

const ReadMoreLink = ({
  href, onClick, text = 'Read more', className,
}) => {
  const onClickLink = (e) => {
    e.stopPropagation();
    onClick();
  };

  return (
    <span className={cx('absolute right-0 bottom-0 bg-white', className)} style={{boxShadow: '-20px 0 9px 1px white'}}>
      <Link
        href={href}
        className="font-medium text-indigo hover:text-indigo focus:text-indigo"
        onClick={onClickLink}
      >
        {`${text} `}
        <ShortRightArrowIcon className="h-3 ml-0.5" />
      </Link>
    </span>
  );
};

const AnswerPreview = ({question}) => {
  const [expanded, setExpanded] = useState(false);
  const {tracker} = useAnalytics();

  const toggleExpanded = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setExpanded(!expanded);
    if (!expanded) tracker.questionListExpandAnswerPreview({id: question.id});
  };

  const clickReadMore = (e) => {
    tracker.questionListClickViewAnswers({question: question.id});
  };

  return question.commenters.length > 0 && (
    <div
      onClick={toggleExpanded}
      onKeyDown={onPressEnter(toggleExpanded)}
      tabIndex={0}
      role="button"
      className="w-full p-2.5 mt-3 border border-gray-200 rounded-lg text-sm font-normal text-left transition"
    >
      <div className="w-full flex justify-between">
        <div className="flex items-center min-w-0">
          <FacePile faces={question.commenters} small className="mr-5" />
          {expanded ? (
            <span className="font-medium">Community answers</span>
          ) : (
            <>
              <P maxLines={1} className="italic w-5/6 pr-1">
                {`"${question.topComment.comment}"`}
              </P>
            </>
          )}
        </div>
        <button
          type="button"
          className="whitespace-nowrap font-medium"
          onClick={toggleExpanded}
        >
          {expanded ? '' : 'View answers'}
          <CaretIcon className={cx('ml-2.5 h-3 transform', {'rotate-90': expanded, '-rotate-90': !expanded})} />
        </button>
      </div>
      <CollapserThing visible={expanded}>
        <P maxLines={3} className="italic mt-2 pr-1">
          {`${question.topComment.displayName} - "${question.topComment.comment}"`}
          <ReadMoreLink
            href={`/questions/${question.id}/${question.slug}#answers`}
            onClick={clickReadMore}
            text="See full answer"
            className="not-italic"
          />
        </P>
      </CollapserThing>
    </div>
  );
};

const TagBar = ({question, query, tagMap}) => {
  const {role, type} = query;
  const {category: categories, roles} = question;

  // Extract just the role names from the roles array (already sorted by frequency)
  // For future reference: r.count gives how many times this question has been asked to this role
  const roleNames = roles.map((r) => r.role);

  // Sort roles with the selected role in front if it exists, then transform to tagMap equivalent
  const sortedRoles = includes(roleNames, role)
    ? uniq([role, ...roleNames]).map((r) => tagMap[r]).filter(Boolean)
    : roleNames.map((r) => tagMap[r]).filter(Boolean);

  // Do the same with categories
  const sortedCategories = includes(categories, type)
    ? uniq([type, ...categories]).map((c) => tagMap[c]).filter(Boolean)
    : categories.map((c) => tagMap[c]).filter(Boolean);

  const tags = compact([
    sortedRoles[0],
    sortedCategories[0],
    ...sortedRoles.slice(1),
    ...sortedCategories.slice(1),
  ]);

  // We will display only the top 2 most relevant tags
  const numAdditionalTags = tags.length > 2 ? tags.length - 2 : 0;

  return (
    <div className="flex flex-wrap gap-x-2 gap-y-1">
      {tags.slice(0, 2).map((t) => (
        <div
          key={t.id}
          className={cx(
            'border rounded-md text-xs font-medium px-2 py-0.5',
            {'border-indigo-100 bg-indigo-100 text-indigo-700': t.id === role || t.id === type},
          )}
        >
          {t.name}
        </div>
      ))}
      {numAdditionalTags > 0 && (
        <div className="border rounded-md text-xs font-medium px-2 py-0.5">
          {`+${numAdditionalTags} more`}
        </div>
      )}
    </div>
  );
};

const ListItem = ({
  onSave,
  isGated,
  question,
  onClick,
  includeAnswers = true,
  query,
  tagMap,
}) => (
  <li>
    <div
      onClick={onClick}
      onKeyDown={onPressEnter(onClick)}
      tabIndex={0}
      role="link"
      className="block cursor-pointer p-4 mb-4 border border-gray-200 rounded-lg text-sm md:text-base font-semibold leading-none hover:border-gray-300 transition duration-50"
    >
      <div className={cx({'pointer-events-none': isGated})}>
        {query?.filterBy === 'pending' && (
          <AdminControls
            question={question}
            className="-mx-4 -mt-4 mb-2 rounded-t-lg"
          />
        )}
        <div className="flex justify-between">
          <div className="flex flex-col justify-between">
            <ListAskedAt companyFiltered={query?.company} tags={question.companies} className="mb-0.5" />
            <div className="mb-2">
              <h3 className="inline align-middle text-lg mr-2">
                <Link
                  href={`/questions/${question.id}/${question.slug}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  {truncate(question.data.title, {length: MAX_TITLE_LENGTH})}
                </Link>
              </h3>
              <span className="align-middle whitespace-nowrap">
                {question.data?.isCoding && <CodingBadge className="mr-2" />}
                {question.difficulty && (
                  <Badge color={DifficultyToBadge[capitalize(question.difficulty)]?.color} className="mr-2">
                    {DifficultyToBadge[capitalize(question.difficulty)]?.text}
                  </Badge>
                )}
              </span>
            </div>
            {/* Desktop & IPad view */}
            <div className="hidden md:block mb-2">
              <TagBar question={question} query={query} tagMap={tagMap} />
            </div>
            <div className="flex">
              <SaveButton
                initialValue={Boolean(question.voted)}
                onClick={(val) => onSave(question.id, val)}
                className="mr-5"
              />
              <CommentCount question={question} />
              <SeenButton question={question} />
            </div>
          </div>

          {question.data?.video?.id && (
            <Link
              className="ml-1 md:mt-1 md:mr-2 md:ml-20"
              href={`/questions/${question.id}/${question.slug}`}
              onClick={(e) => e.stopPropagation()}
            >
              <VideoThumbnail
                imageUrl={getVideoThumbnailURL(question.data.video.id)}
                alt={`Video answer for '${question.data.title}'`}
                className="flex-shrink-0 rounded-lg overflow-hidden w-24 h-16 md:w-32 md:h-20"
              />
            </Link>
          )}
        </div>
        {includeAnswers && <AnswerPreview question={question} />}
        {/* Mobile view */}
        <div className="block md:hidden mt-4">
          {question.category && <TagBar question={question} query={query} tagMap={tagMap} />}
        </div>
      </div>
    </div>
  </li>
);

export default ListItem;
